import React from 'react'
import { isFulfilled } from '@reduxjs/toolkit'
import { l, Modal } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import * as billingActions from 'common/billing/actions/billingActions'
import { AddOnSlug } from 'common/billing/entities/addOns/interfaces'
import { getAddOnsData } from 'common/billing/entities/addOns/selectors'
import { getBillingState } from 'common/billing/entities/miscellaneous/selectors'
import { getBillingProInfo } from 'common/billing/entities/pro/selectors'
import { CancellationReasonType } from 'common/billing/interfaces/cancellationReasons'
import { getUser } from 'common/core/selectors/appSelectors'

import { getModalWidth } from './helpers'
import OfferStep from './steps/OfferStep/OfferStep'
import ReasonListStep from './steps/ReasonListStep/ReasonListStep'
import SuccessStep from './steps/SuccessStep/SuccessStep'
import { TypeSelectorStep } from './steps/TypeSelectorStep'
import WhatLosesStep from './steps/WhatLosesStep/WhatLosesStep'
import { CancelSubscriptionStepType } from './types'

interface CancelSubscriptionModal {
  isOpen: boolean
  onClose: () => void
}

export const CancelSubscriptionModal = (props: CancelSubscriptionModal) => {
  const user = useAppSelector(getUser)
  const {
    cancellationReasonsList: reasons = [],
    period_ends: periodEnds,
    status,
  } = useAppSelector(getBillingProInfo)
  const addOns = useAppSelector(getAddOnsData)
  const aiAddOn = addOns.find((addOn) => addOn.slug === AddOnSlug.AI && addOn.is_enabled)
  const showTypeSelector = Boolean(aiAddOn)
  const aiPrice = aiAddOn?.price.amount ?? 0

  const initialStep = showTypeSelector
    ? CancelSubscriptionStepType.SELECT
    : CancelSubscriptionStepType.REASON_LIST
  const modalRef = React.useRef<HTMLDivElement | null>(null)

  const [step, setStep] = React.useState<CancelSubscriptionStepType>(initialStep)
  const [reason, setReason] = React.useState<CancellationReasonType | null>(null)
  const [description, setDescription] = React.useState('')

  const { fetchingCancelSubscription } = useAppSelector(getBillingState)

  const modalWidth = getModalWidth(step, reason)

  const handleChangeReason = React.useCallback((value: CancellationReasonType) => {
    setReason(value)
  }, [])

  const handleChangeStep = React.useCallback((step: CancelSubscriptionStepType) => {
    setStep(step)
  }, [])

  const handleChangeDescription = React.useCallback((desc: string) => {
    setDescription(desc)
  }, [])

  const handleClose = () => {
    setStep(initialStep)
    setReason(null)
    props.onClose()
  }

  const handleChangeReasonStep = () => {
    if (
      reason &&
      [
        CancellationReasonType.PAGE_PROBLEMS,
        CancellationReasonType.BAD_PRODUCT,
        CancellationReasonType.BAD_RESULT,
        CancellationReasonType.BAD_UNDERSTANDING,
        CancellationReasonType.BAD_EXP,
      ].includes(reason)
    ) {
      return setStep(CancelSubscriptionStepType.OFFER)
    }
    return setStep(CancelSubscriptionStepType.WHAT_LOSES)
  }

  const handleUnsubscribe = async () => {
    if (reason) {
      const cancellationData = { [reason]: description }

      const result = await dispatch(billingActions.cancelSubscription({ data: cancellationData }))

      if (isFulfilled(result)) {
        setStep(CancelSubscriptionStepType.SUCCESS)
      }
    }
  }

  const handleTypeSelectorNext = () => {
    handleChangeStep(CancelSubscriptionStepType.REASON_LIST)
  }

  const dispatch = useAppDispatch()

  const scrollTop = () => {
    if (modalRef.current) {
      modalRef.current.scrollTop = 0
    }
  }

  React.useEffect(() => {
    scrollTop()
  }, [step])

  return (
    <Modal
      title={showTypeSelector ? l.translate('What would you like to cancel?') : ''}
      width={modalWidth}
      open={props.isOpen}
      onRequestClose={handleClose}
      noContentPadding
      overlayRef={modalRef}
    >
      {step === CancelSubscriptionStepType.SELECT && (
        <TypeSelectorStep
          onClose={handleClose}
          onNextStep={handleTypeSelectorNext}
          price={aiPrice}
        />
      )}
      {step === CancelSubscriptionStepType.REASON_LIST && (
        <ReasonListStep
          description={description}
          name={user.first_name}
          reason={reason}
          reasons={reasons}
          onClose={handleClose}
          onNextStep={handleChangeReasonStep}
          onChangeDescription={handleChangeDescription}
          onChange={handleChangeReason}
        />
      )}
      {step === CancelSubscriptionStepType.OFFER && (
        <OfferStep
          reason={reason}
          onClose={handleClose}
          onNextStep={() => handleChangeStep(CancelSubscriptionStepType.WHAT_LOSES)}
        />
      )}
      {step === CancelSubscriptionStepType.WHAT_LOSES && (
        <WhatLosesStep
          loading={fetchingCancelSubscription}
          status={status}
          periodEnds={periodEnds}
          onClose={handleClose}
          onNextStep={handleUnsubscribe}
        />
      )}
      {step === CancelSubscriptionStepType.SUCCESS && (
        <SuccessStep onCancel={handleClose} onSubmit={handleClose} />
      )}
    </Modal>
  )
}
